import type { PackSize, Store, Unit } from '../domain/types.js';

/** One row per store. `sizes` is [qty, unit, label] triples. */
const forStores = (
  ingredientId: string,
  stores: Store[],
  sizes: [number, Unit, string][],
): PackSize[] =>
  stores.flatMap((store) =>
    sizes.map(([packQty, unit, label]) => ({ ingredientId, store, packQty, unit, label })),
  );

const ALL: Store[] = ['aldi', 'tesco', 'lidl'];

export const PACK_SIZES: PackSize[] = [
  // ---------- produce ----------
  ...forStores('onions', ALL, [[5, 'each', 'net of 5']]),
  // baseUnit 'each' means one CLOVE — a bulb is a pack of roughly 12 cloves.
  ...forStores('garlic', ALL, [[12, 'each', '1 bulb']]),
  ...forStores('carrots', ALL, [[1000, 'g', '1kg bag']]),
  ...forStores('potatoes', ALL, [
    [2000, 'g', '2kg bag'],
    [2500, 'g', '2.5kg bag'],
  ]),
  ...forStores('potatoes', ['tesco'], [[5000, 'g', '5kg bag']]),
  ...forStores('sweet-potatoes', ALL, [[1000, 'g', '1kg bag']]),
  ...forStores('broccoli', ALL, [[350, 'g', '1 head']]),
  ...forStores('cauliflower', ALL, [[1, 'each', '1 head']]),
  ...forStores('peppers', ALL, [[3, 'each', 'pack of 3']]),
  ...forStores('mushrooms', ALL, [[300, 'g', '300g punnet']]),
  ...forStores('courgette', ALL, [[2, 'each', 'twin pack']]),
  ...forStores('butternut-squash', ALL, [[1, 'each', '1 squash']]),
  ...forStores('leeks', ALL, [[3, 'each', 'pack of 3']]),
  // baseUnit 'each' means one STICK — a head is roughly 8 sticks.
  ...forStores('celery', ALL, [[8, 'each', '1 head']]),
  ...forStores('spring-onions', ALL, [[6, 'each', 'bunch of 6']]),
  ...forStores('fresh-tomatoes', ALL, [[400, 'g', '400g pack']]),
  ...forStores('cucumber', ALL, [[1, 'each', '1 cucumber']]),
  ...forStores('lettuce', ALL, [[1, 'each', '1 head']]),
  ...forStores('salad-bag', ALL, [[150, 'g', '150g bag']]),
  ...forStores('cabbage', ALL, [[1, 'each', '1 head']]),
  ...forStores('green-beans', ALL, [[200, 'g', '200g pack']]),
  ...forStores('corn-on-the-cob', ALL, [[2, 'each', 'twin pack']]),
  ...forStores('avocado', ALL, [[2, 'each', '2 pack']]),
  ...forStores('bananas', ALL, [[5, 'each', 'pack of 5']]),
  ...forStores('apples', ALL, [[6, 'each', 'pack of 6']]),
  ...forStores('oranges', ALL, [[5, 'each', 'net of 5']]),
  ...forStores('grapes', ALL, [[500, 'g', '500g bag']]),
  ...forStores('strawberries', ALL, [[400, 'g', '400g punnet']]),
  ...forStores('lemons', ALL, [[4, 'each', 'net of 4']]),
  ...forStores('limes', ALL, [[4, 'each', 'net of 4']]),
  ...forStores('ginger', ALL, [[125, 'g', '125g piece']]),
  ...forStores('fresh-coriander', ALL, [[30, 'g', '30g pack']]),
  ...forStores('fresh-basil', ALL, [[30, 'g', '30g pack']]),
  ...forStores('fresh-parsley', ALL, [[30, 'g', '30g pack']]),
  ...forStores('aubergine', ALL, [[1, 'each', '1 aubergine']]),

  // ---------- meat_fish ----------
  ...forStores('beef-mince', ['aldi', 'lidl'], [
    [500, 'g', '500g pack'],
    [1000, 'g', '1kg pack'],
  ]),
  ...forStores('beef-mince', ['tesco'], [
    [500, 'g', '500g pack'],
    [750, 'g', '750g pack'],
  ]),
  ...forStores('lamb-mince', ['aldi'], [
    [500, 'g', '500g pack'],
    [1000, 'g', '1kg pack'],
  ]),
  ...forStores('lamb-mince', ['lidl'], [
    [400, 'g', '400g pack'],
    [500, 'g', '500g pack'],
  ]),
  ...forStores('lamb-mince', ['tesco'], [
    [500, 'g', '500g pack'],
    [750, 'g', '750g pack'],
  ]),
  ...forStores('pork-mince', ['aldi', 'lidl'], [
    [500, 'g', '500g pack'],
    [750, 'g', '750g pack'],
  ]),
  ...forStores('pork-mince', ['tesco'], [
    [500, 'g', '500g pack'],
    [1000, 'g', '1kg pack'],
  ]),
  ...forStores('turkey-mince', ['aldi', 'lidl'], [
    [500, 'g', '500g pack'],
    [750, 'g', '750g pack'],
  ]),
  ...forStores('turkey-mince', ['tesco'], [
    [500, 'g', '500g pack'],
    [1000, 'g', '1kg pack'],
  ]),
  ...forStores('diced-beef', ALL, [[400, 'g', '400g pack']]),
  ...forStores('beef-steak', ALL, [[300, 'g', 'pack of 2 steaks']]),
  ...forStores('chicken-breast', ['aldi', 'lidl'], [
    [650, 'g', '2 fillets (approx 650g)'],
    [1000, 'g', '1kg family pack'],
  ]),
  ...forStores('chicken-breast', ['tesco'], [
    [600, 'g', '2 fillets (approx 600g)'],
    [750, 'g', '750g pack'],
  ]),
  ...forStores('chicken-thighs', ['aldi', 'lidl'], [
    [500, 'g', '500g pack'],
    [1000, 'g', '1kg family pack'],
  ]),
  ...forStores('chicken-thighs', ['tesco'], [
    [500, 'g', '500g pack'],
    [700, 'g', '700g pack'],
  ]),
  ...forStores('whole-chicken', ALL, [[1, 'each', '1 whole chicken']]),
  ...forStores('sausages', ['aldi', 'lidl'], [[8, 'each', 'pack of 8']]),
  ...forStores('sausages', ['tesco'], [[6, 'each', 'pack of 6']]),
  ...forStores('bacon', ['aldi', 'lidl'], [[250, 'g', '250g pack']]),
  ...forStores('bacon', ['tesco'], [[300, 'g', '300g pack']]),
  ...forStores('gammon', ALL, [[750, 'g', '750g gammon joint']]),
  ...forStores('pork-chops', ALL, [[4, 'each', 'pack of 4']]),
  ...forStores('chorizo', ALL, [[225, 'g', '225g ring']]),
  ...forStores('ham-slices', ALL, [[125, 'g', '125g pack']]),
  ...forStores('cooked-chicken-slices', ALL, [[125, 'g', '125g pack']]),
  ...forStores('salmon-fillets', ALL, [[2, 'each', 'pack of 2 fillets']]),
  ...forStores('cod-fillets', ALL, [[2, 'each', 'pack of 2 fillets']]),
  ...forStores('white-fish-fillets', ALL, [[2, 'each', 'pack of 2 fillets']]),
  ...forStores('prawns', ALL, [[200, 'g', '200g pack']]),
  ...forStores('beef-joint', ALL, [[1000, 'g', '1kg joint']]),
  ...forStores('pork-joint', ALL, [[1000, 'g', '1kg joint']]),
  ...forStores('smoked-haddock', ALL, [[300, 'g', '300g pack']]),

  // ---------- dairy_chilled ----------
  ...forStores('milk', ['aldi', 'lidl'], [
    [1136, 'ml', '2 pint bottle'],
    [2272, 'ml', '4 pint bottle'],
  ]),
  ...forStores('milk', ['tesco'], [
    [1136, 'ml', '2 pint bottle'],
    [2272, 'ml', '4 pint bottle'],
    [3408, 'ml', '6 pint bottle'],
  ]),
  ...forStores('butter', ALL, [[250, 'g', '250g block']]),
  ...forStores('cheddar', ['aldi', 'lidl'], [
    [200, 'g', '200g block'],
    [400, 'g', '400g block'],
  ]),
  ...forStores('cheddar', ['tesco'], [
    [250, 'g', '250g block'],
    [400, 'g', '400g block'],
  ]),
  ...forStores('mozzarella', ['aldi', 'lidl'], [
    [125, 'g', '125g ball'],
    [200, 'g', '200g grated bag'],
  ]),
  ...forStores('mozzarella', ['tesco'], [
    [150, 'g', '150g ball'],
    [200, 'g', '200g grated bag'],
  ]),
  ...forStores('parmesan', ALL, [
    [40, 'g', '40g pot'],
    [150, 'g', '150g wedge'],
  ]),
  ...forStores('cream-cheese', ALL, [[200, 'g', '200g tub']]),
  ...forStores('creme-fraiche', ALL, [[300, 'g', '300g tub']]),
  ...forStores('double-cream', ALL, [[300, 'ml', '300ml pot']]),
  ...forStores('mascarpone', ALL, [[250, 'g', '250g tub']]),
  ...forStores('yoghurt-pots', ALL, [[6, 'each', 'pack of 6']]),
  ...forStores('greek-yoghurt', ALL, [[500, 'g', '500g tub']]),
  ...forStores('eggs', ['aldi', 'lidl'], [
    [6, 'each', 'box of 6'],
    [12, 'each', 'box of 12'],
  ]),
  ...forStores('eggs', ['tesco'], [
    [6, 'each', 'box of 6'],
    [10, 'each', 'box of 10'],
    [12, 'each', 'box of 12'],
  ]),
  ...forStores('puff-pastry', ALL, [[320, 'g', '320g ready-rolled sheet']]),
  ...forStores('shortcrust-pastry', ALL, [[500, 'g', '500g block']]),

  // ---------- bakery ----------
  // baseUnit 'each' means one SLICE — an 800g loaf is roughly 16 slices,
  // and these stores also sell a half/small loaf of roughly 8 slices.
  ...forStores('bread-white', ALL, [
    [16, 'each', '800g loaf'],
    [8, 'each', '400g small loaf'],
  ]),
  ...forStores('bread-wholemeal', ALL, [
    [16, 'each', '800g loaf'],
    [8, 'each', '400g small loaf'],
  ]),
  ...forStores('bread-rolls', ALL, [[6, 'each', 'pack of 6']]),
  ...forStores('burger-buns', ALL, [[4, 'each', 'pack of 4']]),
  ...forStores('hot-dog-buns', ALL, [[4, 'each', 'pack of 4']]),
  ...forStores('wraps', ALL, [[6, 'each', 'pack of 6']]),
  ...forStores('pitta-bread', ALL, [[6, 'each', 'pack of 6']]),
  ...forStores('bagels', ALL, [[5, 'each', 'pack of 5']]),
  ...forStores('baguette', ALL, [[1, 'each', '1 baguette']]),
  ...forStores('crumpets', ALL, [[6, 'each', 'pack of 6']]),

  // ---------- ambient ----------
  ...forStores('pasta-spaghetti', ['aldi', 'lidl'], [
    [500, 'g', '500g pack'],
    [1000, 'g', '1kg pack'],
  ]),
  ...forStores('pasta-spaghetti', ['tesco'], [
    [500, 'g', '500g pack'],
    [750, 'g', '750g pack'],
  ]),
  ...forStores('pasta-penne', ['aldi', 'lidl'], [
    [500, 'g', '500g pack'],
    [1000, 'g', '1kg pack'],
  ]),
  ...forStores('pasta-penne', ['tesco'], [
    [500, 'g', '500g pack'],
    [750, 'g', '750g pack'],
  ]),
  ...forStores('pasta-fusilli', ['aldi', 'lidl'], [
    [500, 'g', '500g pack'],
    [1000, 'g', '1kg pack'],
  ]),
  ...forStores('pasta-fusilli', ['tesco'], [
    [500, 'g', '500g pack'],
    [750, 'g', '750g pack'],
  ]),
  ...forStores('lasagne-sheets', ALL, [[250, 'g', '250g box']]),
  ...forStores('rice-basmati', ['aldi', 'lidl'], [
    [1000, 'g', '1kg bag'],
    [2000, 'g', '2kg bag'],
  ]),
  ...forStores('rice-basmati', ['tesco'], [
    [1000, 'g', '1kg bag'],
    [5000, 'g', '5kg bag'],
  ]),
  ...forStores('rice-long-grain', ['aldi', 'lidl'], [
    [1000, 'g', '1kg bag'],
    [2000, 'g', '2kg bag'],
  ]),
  ...forStores('rice-long-grain', ['tesco'], [
    [500, 'g', '500g bag'],
    [1000, 'g', '1kg bag'],
  ]),
  ...forStores('rice-risotto', ALL, [
    [500, 'g', '500g bag'],
    [1000, 'g', '1kg bag'],
  ]),
  ...forStores('noodles-egg', ALL, [[300, 'g', '300g nest pack']]),
  ...forStores('couscous', ALL, [[500, 'g', '500g bag']]),
  ...forStores('chopped-tomatoes', ALL, [
    [400, 'g', '400g tin'],
    [1600, 'g', '4-pack of tins'],
  ]),
  ...forStores('passata', ALL, [[700, 'g', '700g bottle']]),
  ...forStores('tomato-puree', ALL, [[200, 'g', '200g tube']]),
  ...forStores('baked-beans', ALL, [
    [415, 'g', '415g tin'],
    [1660, 'g', '4-pack of tins'],
  ]),
  ...forStores('corned-beef', ALL, [[340, 'g', '340g tin']]),
  ...forStores('dried-yeast', ALL, [[7, 'g', '7g sachet']]),
  ...forStores('kidney-beans', ALL, [[400, 'g', '400g tin']]),
  ...forStores('chickpeas', ALL, [[400, 'g', '400g tin']]),
  ...forStores('lentils-red', ALL, [[500, 'g', '500g bag']]),
  ...forStores('sweetcorn-tinned', ALL, [[325, 'g', '325g tin']]),
  ...forStores('tuna-tinned', ALL, [
    [145, 'g', '145g tin'],
    [580, 'g', '4-pack of tins'],
  ]),
  ...forStores('coconut-milk', ALL, [[400, 'ml', '400ml tin']]),
  ...forStores('stock-cubes-chicken', ALL, [[12, 'each', 'box of 12 cubes']]),
  ...forStores('stock-cubes-beef', ALL, [[12, 'each', 'box of 12 cubes']]),
  ...forStores('stock-cubes-vegetable', ALL, [[12, 'each', 'box of 12 cubes']]),
  ...forStores('olive-oil', ALL, [
    [500, 'ml', '500ml bottle'],
    [1000, 'ml', '1 litre bottle'],
  ]),
  ...forStores('vegetable-oil', ALL, [[1000, 'ml', '1 litre bottle']]),
  ...forStores('plain-flour', ALL, [[1500, 'g', '1.5kg bag']]),
  ...forStores('cornflour', ALL, [[200, 'g', '200g tub']]),
  ...forStores('sugar', ALL, [[1000, 'g', '1kg bag']]),
  ...forStores('salt', ALL, [[750, 'g', '750g tub']]),
  ...forStores('black-pepper', ALL, [[100, 'g', '100g pot']]),
  ...forStores('paprika', ALL, [[40, 'g', '40g pot']]),
  ...forStores('smoked-paprika', ALL, [[40, 'g', '40g pot']]),
  ...forStores('cumin', ALL, [[40, 'g', '40g pot']]),
  ...forStores('coriander-ground', ALL, [[30, 'g', '30g pot']]),
  ...forStores('turmeric', ALL, [[40, 'g', '40g pot']]),
  ...forStores('curry-powder', ALL, [[80, 'g', '80g pot']]),
  ...forStores('chilli-powder', ALL, [[40, 'g', '40g pot']]),
  ...forStores('mixed-herbs', ALL, [[15, 'g', '15g pot']]),
  ...forStores('oregano', ALL, [[15, 'g', '15g pot']]),
  ...forStores('cinnamon', ALL, [[40, 'g', '40g pot']]),
  ...forStores('soy-sauce', ALL, [[150, 'ml', '150ml bottle']]),
  ...forStores('worcestershire-sauce', ALL, [[150, 'ml', '150ml bottle']]),
  ...forStores('vinegar', ALL, [[568, 'ml', '568ml bottle']]),
  ...forStores('honey', ALL, [[340, 'g', '340g jar']]),
  ...forStores('mustard', ALL, [[185, 'g', '185g jar']]),
  ...forStores('tomato-ketchup', ALL, [
    [460, 'ml', '460ml bottle'],
    [910, 'ml', '910ml bottle'],
  ]),
  ...forStores('mayonnaise', ALL, [[400, 'g', '400g jar']]),
  ...forStores('gravy-granules', ALL, [[200, 'g', '200g pot']]),
  ...forStores('curry-paste-korma', ALL, [[260, 'g', '260g jar']]),
  ...forStores('curry-paste-thai-green', ALL, [[260, 'g', '260g jar']]),
  ...forStores('katsu-sauce', ALL, [[230, 'g', '230g jar']]),
  ...forStores('sweet-sour-sauce', ALL, [[500, 'g', '500g jar']]),
  ...forStores('pesto', ALL, [[190, 'g', '190g jar']]),
  ...forStores('breadcrumbs', ALL, [[250, 'g', '250g pack']]),
  ...forStores('peanut-butter', ALL, [[340, 'g', '340g jar']]),
  ...forStores('jam', ALL, [[370, 'g', '370g jar']]),
  ...forStores('cereal', ALL, [[500, 'g', '500g box']]),
  ...forStores('porridge-oats', ALL, [[1000, 'g', '1kg bag']]),
  ...forStores('crisps', ALL, [[6, 'each', 'multipack of 6']]),
  ...forStores('biscuits', ALL, [[300, 'g', '300g pack']]),
  ...forStores('cereal-bars', ALL, [[6, 'each', 'box of 6']]),
  ...forStores('raisins', ALL, [[500, 'g', '500g bag']]),

  // ---------- frozen ----------
  ...forStores('peas-frozen', ALL, [[1000, 'g', '1kg bag']]),
  ...forStores('sweetcorn-frozen', ALL, [[1000, 'g', '1kg bag']]),
  ...forStores('mixed-veg-frozen', ALL, [[1000, 'g', '1kg bag']]),
  ...forStores('chips-frozen', ALL, [
    [1000, 'g', '1kg bag'],
    [1500, 'g', '1.5kg bag'],
  ]),
  ...forStores('potato-smiles', ALL, [[750, 'g', '750g bag']]),
  ...forStores('fish-fingers', ALL, [[10, 'each', 'pack of 10']]),
  ...forStores('chicken-nuggets', ALL, [[20, 'each', 'pack of 20']]),
  ...forStores('garlic-bread', ALL, [[2, 'each', 'twin baguette pack']]),
  ...forStores('ice-cream', ALL, [[900, 'ml', '900ml tub']]),
  ...forStores('frozen-berries', ALL, [[500, 'g', '500g bag']]),

  // ---------- drinks ----------
  ...forStores('orange-juice', ['aldi', 'lidl'], [[1000, 'ml', '1 litre carton']]),
  ...forStores('orange-juice', ['tesco'], [
    [1000, 'ml', '1 litre carton'],
    [1750, 'ml', '1.75 litre carton'],
  ]),
  ...forStores('apple-juice', ALL, [[1000, 'ml', '1 litre carton']]),
  ...forStores('squash', ALL, [[1000, 'ml', '1 litre bottle']]),
  ...forStores('water-bottles', ALL, [[24, 'each', 'pack of 24 x 500ml']]),
  ...forStores('coffee', ALL, [[200, 'g', '200g jar']]),
  ...forStores('tea-bags', ALL, [[80, 'each', 'box of 80']]),

  // ---------- household ----------
  ...forStores('bin-bags', ALL, [[20, 'each', 'roll of 20']]),
  ...forStores('washing-up-liquid', ALL, [[500, 'ml', '500ml bottle']]),
  ...forStores('kitchen-roll', ['aldi', 'lidl'], [[2, 'each', 'pack of 2 rolls']]),
  ...forStores('kitchen-roll', ['tesco'], [[4, 'each', 'pack of 4 rolls']]),
  ...forStores('toilet-roll', ['aldi', 'lidl'], [[9, 'each', 'pack of 9 rolls']]),
  ...forStores('toilet-roll', ['tesco'], [
    [9, 'each', 'pack of 9 rolls'],
    [16, 'each', 'pack of 16 rolls'],
  ]),
  ...forStores('nappies', ALL, [[44, 'each', 'pack of 44']]),
  ...forStores('wipes', ALL, [[56, 'each', 'pack of 56']]),
  ...forStores('dishwasher-tablets', ALL, [[30, 'each', 'pack of 30']]),
  ...forStores('foil', ALL, [[1, 'each', '1 roll']]),
];
